import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  BAR_CHART_OPTION,
  EXPANDED_GROUPED_BAR_CHART_OPTION,
  INIT_KPI_CHART_OPTION_LARGE,
  KPI_EXPANDED_BAR_CHART_OPTION,
  PIE_CHART_COLOR_MAPPING,
  PIE_CHART_OPTION
} from '@catalogues';
import { ChartSize, KpiChartData, KpiKeys } from '@models';
import { ChartFilterIntegrationService } from '@services';
import { KpiChartFacadeService } from 'app/services/kpi-chart-facade/kpi-chart-facade.service';
import type { EChartsOption } from 'echarts';
import { Observable, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'expanded-view',
  templateUrl: './expanded-view.component.html',
  styleUrl: './expanded-view.component.scss'
})
export class ExpandedViewComponent implements OnInit, OnDestroy {
  @Input() viewTitle = '';
  @Input() viewDescription?: string = '';
  @Input() chartOption?: any;
  @Input() chartData?: any;
  @Input() chartTitle = '';

  // Injected services
  dialogRef = inject(MatDialogRef<ExpandedViewComponent>);
  data: any = inject(MAT_DIALOG_DATA);
  filterService = inject(ChartFilterIntegrationService);
  private kpiChartFacade = inject(KpiChartFacadeService);

  private destroy$ = new Subject<void>();

  bar: EChartsOption = BAR_CHART_OPTION;
  kpiBar: EChartsOption = KPI_EXPANDED_BAR_CHART_OPTION;
  pie: EChartsOption = PIE_CHART_OPTION;
  groupedBar: EChartsOption = EXPANDED_GROUPED_BAR_CHART_OPTION;
  colorMap = PIE_CHART_COLOR_MAPPING;
  chartSize: ChartSize = 'large';
  initKpiOptionLarge = INIT_KPI_CHART_OPTION_LARGE;

  sortedData$!: Observable<any>;
  reactiveChartData$!: Observable<KpiChartData | null>;

  currentChartData: KpiChartData | null = null;
  currentExpandedData: any = null;
  currentKpiKey?: KpiKeys;

  ngOnInit() {
    this.currentKpiKey = this.data?.kpiKey ?? undefined;
    this.reactiveChartData$ = this.kpiChartFacade.activeKpiChartData$;

    if (!this.currentKpiKey) {
      this.kpiChartFacade.activeKpiKey$.pipe(takeUntil(this.destroy$)).subscribe((kpiKey) => {
        this.currentKpiKey = kpiKey ?? undefined;
        this.updateExpandedData();
      });
    }

    this.reactiveChartData$.pipe(takeUntil(this.destroy$)).subscribe((chartData) => {
      this.currentChartData = chartData;
      this.updateExpandedData();
    });

    this.kpiChartFacade.toggleStates$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (
        (this.data.chartType === 'toggle' || this.data.chartType === 'trend') &&
        this.currentKpiKey
      ) {
        this.updateExpandedData();
      }
    });

    if (this.data && this.data.sortedData$) {
      this.sortedData$ = this.data.sortedData$;
    }

    this.updateExpandedData();

    if (this.data.chartType === 'toggle' && this.currentKpiKey) {
      setTimeout(() => {
        this.updateExpandedData();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateExpandedData(): void {
    if (!this.currentChartData || !this.data) return;

    switch (this.data.chartType) {
      case 'trend':
        this.currentExpandedData = this.getReactiveTrendChartData();
        break;

      case 'bar':
        this.currentExpandedData = this.getReactiveBarChartData(this.data.chartId);
        break;

      case 'pie':
        this.currentExpandedData = this.getReactivePieChartData(this.data.chartId);
        break;

      case 'groupedBar':
        this.currentExpandedData = this.getReactiveGroupedBarChartData(this.data.chartId);
        break;

      case 'table':
        this.currentExpandedData = this.currentChartData.charts?.keyMilestonesTable;
        break;

      case 'toggle':
        this.currentExpandedData = this.getReactiveToggleChartData(this.data.chartId);
        if (!this.currentExpandedData && this.data.toggleChartData && this.currentKpiKey) {
          this.applySavedToggleState(this.data.toggleChartData);
          this.currentExpandedData = this.data.toggleChartData;
        }
        break;

      default:
        // Fallback to static data for unsupported chart types
        this.currentExpandedData =
          this.data.expandedBarChartData ||
          this.data.expandedPieChartData ||
          this.data.expandedGroupedBarChartData ||
          this.data.expandedTrendChartData ||
          this.data.toggleChartData ||
          this.data.tableData;
    }
  }

  private getReactiveBarChartData(chartId: string): any {
    return this.currentChartData?.charts?.bar?.find(
      (chartData: any) => chartData.identifier === chartId
    );
  }

  private getReactiveGroupedBarChartData(chartId: string): any {
    return this.currentChartData?.charts?.groupedBar?.find(
      (chartData: any) => chartData.identifier === chartId
    );
  }

  private getReactivePieChartData(chartId: string): any {
    return this.currentChartData?.charts?.pie?.find(
      (chartData: any) => chartData.identifier === chartId
    );
  }

  private getReactiveTrendChartData(): any {
    const originalTrendData = this.currentChartData?.charts?.trendchart;

    if (!originalTrendData) return null;

    const trendData = JSON.parse(JSON.stringify(originalTrendData));

    if (!Object.prototype.hasOwnProperty.call(trendData, 'initialIsPercentChart')) {
      trendData.initialIsPercentChart = originalTrendData.isPercentChart;
    }

    if (trendData.hasToggle && this.currentKpiKey) {
      const savedState = this.kpiChartFacade.getToggleState(
        this.currentKpiKey,
        trendData.identifier
      );

      if (savedState) {
        this.kpiChartFacade.applyTrendChartToggleState(this.currentKpiKey, trendData);
      }
    }

    return trendData;
  }

  private getReactiveToggleChartData(chartId: string): any {
    const originalToggleData = this.currentChartData?.charts?.toggleChart?.find(
      (chartData: any) => chartData.identifier === chartId
    );

    if (!originalToggleData) return null;

    const toggleData = JSON.parse(JSON.stringify(originalToggleData));

    if (this.currentKpiKey) {
      const savedState = this.kpiChartFacade.getToggleState(
        this.currentKpiKey,
        toggleData.identifier
      );

      if (savedState) {
        toggleData.charts.primaryChart.selected = savedState === toggleData.charts.primaryChart.id;
        toggleData.charts.secondaryChart.selected =
          savedState === toggleData.charts.secondaryChart.id;
      } else {
        toggleData.charts.primaryChart.selected = true;
        toggleData.charts.secondaryChart.selected = false;
      }

      this.data.viewDescription = toggleData.charts.primaryChart.selected
        ? toggleData.charts.primaryChart.data.tooltipText
        : toggleData.charts.secondaryChart.data.tooltipText;
    }

    return toggleData;
  }

  private applySavedToggleState(toggleChartData: any): void {
    if (!toggleChartData || !this.currentKpiKey) return;

    const savedState = this.kpiChartFacade.getToggleState(
      this.currentKpiKey,
      toggleChartData.identifier
    );

    if (savedState) {
      toggleChartData.charts.primaryChart.selected =
        savedState === toggleChartData.charts.primaryChart.id;
      toggleChartData.charts.secondaryChart.selected =
        savedState === toggleChartData.charts.secondaryChart.id;
    } else {
      toggleChartData.charts.primaryChart.selected = true;
      toggleChartData.charts.secondaryChart.selected = false;
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
